import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Users, DollarSign, Award, ArrowRight, Bell } from 'lucide-react';

const STAT_CONFIG = [
  { key: 'verification', icon: ShieldCheck, color: 'blue', label: 'Verification',
    render: (s) => ({ value: `${s?.verification_percentage || 0}%`, sub: `${s?.verified_badges || 0} of ${s?.total_badges || 0} badges verified`, progress: s?.verification_percentage || 0 })},
  { key: 'referrals', icon: Users, color: 'green', label: 'Referrals',
    render: (s) => ({ value: s?.successful_referrals || 0, sub: `${s?.referral_count || 0} total referrals` })},
  { key: 'earnings', icon: DollarSign, color: 'purple', label: 'Earnings',
    render: (s) => ({ value: `$${s?.referral_earnings?.toFixed(2) || '0.00'}`, sub: 'From referrals' })},
  { key: 'membership', icon: Award, color: 'orange', label: 'Membership',
    render: (s) => ({ value: s?.membership_tier || 'Free', sub: 'Current plan', capitalize: true })},
];

export const DashboardStatsGrid = ({ stats }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
    {STAT_CONFIG.map(({ key, icon: Icon, color, label, render }, i) => {
      const d = render(stats);
      return (
        <motion.div key={key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * (i + 1) }}
          className="bg-white border border-slate-200 rounded-md p-6 shadow-sm" data-testid={`${key}-stat`}>
          <div className="flex items-center justify-between mb-4">
            <div className={`bg-${color}-50 p-3 rounded-md`}><Icon className={`h-6 w-6 text-${color}-500`} /></div>
            <span className="text-sm font-medium text-slate-600">{label}</span>
          </div>
          <div className={`text-${key === 'membership' ? '2xl' : '3xl'} font-bold font-heading text-slate-900 ${d.capitalize ? 'capitalize' : ''}`}>{d.value}</div>
          <p className="text-sm text-slate-600 mt-1">{d.sub}</p>
          {d.progress !== undefined && (
            <div className="w-full bg-slate-100 rounded-full h-2 mt-2">
              <div className="bg-blue-500 h-2 rounded-full transition-all duration-500" style={{ width: `${d.progress}%` }} />
            </div>
          )}
        </motion.div>
      );
    })}
  </div>
);

export const DashboardQuickActions = ({ quickActions }) => (
  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}
    className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6 shadow-sm">
    <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Quick Actions</h2>
    <div className="grid grid-cols-2 gap-4">
      {quickActions.map((action) => (
        <Link key={action.label} to={action.link}
          className="flex items-center gap-3 p-4 border border-slate-200 rounded-md hover:border-blue-300 hover:shadow-md transition-all card-hover"
          data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className={`p-2 rounded-md ${action.color}`}><action.icon className="h-5 w-5" /></div>
          <span className="font-medium text-slate-900">{action.label}</span>
        </Link>
      ))}
    </div>
  </motion.div>
);

export const DashboardNotifications = ({ notifications }) => (
  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}
    className="bg-white border border-slate-200 rounded-md p-6 shadow-sm" data-testid="notifications-panel">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-heading text-xl font-bold text-slate-900">Notifications</h2>
      <Bell className="h-5 w-5 text-slate-400" />
    </div>
    <div className="space-y-3">
      {notifications.map((notif) => (
        <div key={notif.message}
          className={`p-3 rounded-md border ${notif.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}
          data-testid={`notification-${notif.type}`}>
          <p className="text-sm text-slate-700">{notif.message}</p>
        </div>
      ))}
    </div>
  </motion.div>
);

export const DashboardCTA = () => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
    className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md p-8 shadow-lg">
    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
      <div>
        <h3 className="font-heading text-2xl font-bold mb-2">Your Business Profile</h3>
        <p className="text-blue-100">Click the button to view business profile page.</p>
      </div>
      <Link to="/profile" className="bg-white text-blue-600 px-6 py-3 rounded-md font-semibold hover:bg-blue-50 transition-all btn-hover-lift whitespace-nowrap" data-testid="upgrade-cta">
        View Profile <ArrowRight className="inline ml-2 h-5 w-5" />
      </Link>
    </div>
  </motion.div>
);
